/**
 * v137 — Spine What-If panel.
 *
 * Diagnostic surface for the Build Review (`SpineCheck`) and the Spine
 * Calculator View. Rendered inline below the main verdict when the user's
 * selected spine is out-of-window. Two sections:
 *
 *   A · Single-spine dropdown — cycle through the standard ladder and see
 *       the live verdict for whichever value is picked.
 *
 *   B · "Show all spines" expandable matrix — a table (desktop) / vertical
 *       card stack (mobile) showing every spine in the ladder with its
 *       verdict at the user's current setup.
 *
 * Diagnostic-only: the panel never mutates the user's actual saved spine.
 * A small Sal-voice nudge tells them where to make the change permanent.
 *
 * Uses the v135.1 unified algorithm via `computeSpineMatrix`. Memoized on
 * the inputs so it doesn't recompute on every render.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import clsx from 'clsx';
import {
  computeSpineMatrix,
  defaultSuggestedSpine,
  noStandardSpineFits,
  NO_STANDARD_SPINE_FITS_COPY,
  SPINE_LADDER,
  type SpineMatrixInputs,
  type SpineMatrixRow,
} from '@/lib/spineMatrix';

interface SpineWhatIfPanelProps {
  inputs: SpineMatrixInputs;
  /** The user's currently-selected spine — highlighted in the matrix. */
  currentSpine: number;
  /**
   * Optional callback when the user picks a try-it spine in the dropdown.
   * Right now the panel is purely diagnostic, so this is unused — but
   * passing it lets a parent wire "apply this spine to my build" later.
   */
  onTryitChange?: (spine: number) => void;
  /** Visual variant. 'card' is the default inline treatment for SpineCheck. */
  variant?: 'card' | 'panel';
}

/** Visual treatments for each verdict tier. Matches the rest of the app. */
const VERDICT_META: Record<
  SpineMatrixRow['verdict'],
  { label: string; chip: string; pill: string; icon: string }
> = {
  on: {
    label: 'In range',
    chip: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    pill: 'text-emerald-400',
    icon: '✓',
  },
  'slightly-stiff': {
    label: 'Borderline · slightly stiff',
    chip: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    pill: 'text-amber-300',
    icon: '↑',
  },
  'slightly-weak': {
    label: 'Borderline · slightly weak',
    chip: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    pill: 'text-amber-300',
    icon: '↓',
  },
  'too-stiff': {
    label: 'Too stiff',
    chip: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    pill: 'text-rose-400',
    icon: '⤴',
  },
  'too-weak': {
    label: 'Too weak',
    chip: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    pill: 'text-rose-400',
    icon: '⤵',
  },
};

export function SpineWhatIfPanel({
  inputs,
  currentSpine,
  onTryitChange,
  variant = 'card',
}: SpineWhatIfPanelProps) {
  const matrix = useMemo(() => computeSpineMatrix(inputs), [inputs]);

  const [tryitSpine, setTryitSpine] = useState<number>(() =>
    defaultSuggestedSpine(matrix),
  );

  // If inputs change, re-default the try-it pick (the previous default may
  // now be a bad option for the new setup).
  useMemo(() => {
    const stillValid = matrix.some((r) => r.spine === tryitSpine);
    if (!stillValid) setTryitSpine(defaultSuggestedSpine(matrix));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix]);

  const [showAll, setShowAll] = useState(false);

  const noFit = noStandardSpineFits(matrix);
  const tryitRow = matrix.find((r) => r.spine === tryitSpine) ?? matrix[0];
  const tryitMeta = VERDICT_META[tryitRow.verdict];

  const handleTryitPick = (spine: number) => {
    setTryitSpine(spine);
    onTryitChange?.(spine);
  };

  return (
    <div
      className={clsx(
        'rounded-md border border-zinc-800 bg-zinc-950/40',
        variant === 'card' ? 'mt-4 p-4' : 'p-5',
      )}
      data-testid="spine-whatif"
    >
      <div className="flex items-baseline gap-2 mb-3">
        <Search className="w-3.5 h-3.5 text-cyan-400" />
        <h3 className="text-[11px] font-mono uppercase tracking-[3px] text-cyan-300">
          Find your in-window spine
        </h3>
      </div>

      {/* ─── A · Single-spine dropdown ───────────────────────────────── */}
      <div className="space-y-2">
        <label className="text-sm text-zinc-300 block">
          What if you used a different spine?
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative inline-flex">
            <select
              value={tryitSpine}
              onChange={(e) => handleTryitPick(Number(e.target.value))}
              className="appearance-none min-h-[44px] pl-3 pr-9 py-2 bg-zinc-900 border border-zinc-700 rounded-md text-sm font-mono text-zinc-100 focus:border-cyan-500 outline-none"
              aria-label="Try a different spine"
            >
              {SPINE_LADDER.map((spine) => (
                <option key={spine} value={spine}>
                  {spine} spine
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
            />
          </div>
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-[12px] font-mono uppercase tracking-wider',
              tryitMeta.chip,
            )}
          >
            <span aria-hidden>{tryitMeta.icon}</span>
            {tryitMeta.label}
          </span>
        </div>
        <p className="text-[12px] text-zinc-400 leading-relaxed">
          At your current setup (effective {tryitRow.effectiveDrawWeight.toFixed(0)} lb),
          a spine of <strong className="text-zinc-100">{tryitSpine}</strong> would be:{' '}
          <span className={tryitMeta.pill}>{tryitMeta.label.toLowerCase()}</span>.
        </p>
      </div>

      {/* ─── B · Show all spines (expandable) ───────────────────────── */}
      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        className="mt-4 min-h-[44px] w-full flex items-center justify-between gap-2 px-3 py-2 border border-zinc-800 rounded text-sm text-zinc-300 hover:bg-zinc-900/60"
        aria-expanded={showAll}
      >
        <span className="text-left">
          {showAll ? 'Hide' : 'Show'} all spines · all ten standard rungs at your setup
        </span>
        {showAll ? (
          <ChevronUp size={14} className="text-zinc-500" />
        ) : (
          <ChevronDown size={14} className="text-zinc-500" />
        )}
      </button>

      {showAll && (
        <div className="mt-3">
          {/* Desktop: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                  <th className="text-left py-2 px-2">Spine</th>
                  <th className="text-left py-2 px-2">Verdict</th>
                  <th className="text-left py-2 px-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => {
                  const meta = VERDICT_META[row.verdict];
                  const isCurrent = row.spine === currentSpine;
                  return (
                    <tr
                      key={row.spine}
                      className={clsx(
                        'border-b border-zinc-800/50',
                        isCurrent && 'bg-ember-500/5',
                      )}
                    >
                      <td className="py-2 px-2 font-mono text-zinc-100">
                        {row.spine}
                        {isCurrent && (
                          <span className="text-[10px] text-ember-400 ml-1.5 align-middle">
                            current
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-mono uppercase tracking-wider',
                            meta.chip,
                          )}
                        >
                          <span aria-hidden>{meta.icon}</span>
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-[12px] text-zinc-400 leading-snug">
                        {summarizeRow(row)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: vertical card stack */}
          <div className="sm:hidden space-y-2">
            {matrix.map((row) => {
              const meta = VERDICT_META[row.verdict];
              const isCurrent = row.spine === currentSpine;
              return (
                <div
                  key={row.spine}
                  className={clsx(
                    'rounded-md border border-zinc-800 bg-zinc-950 p-3',
                    isCurrent && 'border-ember-500/40 bg-ember-500/5',
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <p className="font-mono text-zinc-100">
                      {row.spine}
                      {isCurrent && (
                        <span className="text-[10px] text-ember-400 ml-1.5 align-middle">
                          current
                        </span>
                      )}
                    </p>
                    <span
                      className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider',
                        meta.chip,
                      )}
                    >
                      <span aria-hidden>{meta.icon}</span>
                      {meta.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 leading-snug">
                    {summarizeRow(row)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Helpful copy when no standard spine fits ────────────────── */}
      {noFit && (
        <div className="mt-3 px-3 py-2.5 border border-rose-500/30 bg-rose-500/5 rounded-md text-[12px] text-rose-100 leading-relaxed">
          {NO_STANDARD_SPINE_FITS_COPY}
        </div>
      )}

      {/* Persistent footer note */}
      <p className="mt-3 text-[11px] text-zinc-500 italic leading-relaxed">
        This is a tuning-bench what-if — your selected spine isn't changed here.
        To make a swap permanent, head to the Bow Setup tab and change the
        spine on the active arrow.
      </p>
    </div>
  );
}

/**
 * One-line description per row. Mirrors the chart-range pair so the user
 * can see "your setup wants 250–300" at every row instead of re-reading
 * the long message text for each.
 */
function summarizeRow(row: SpineMatrixRow): string {
  if (row.verdict === 'on') {
    return `In the ${row.recommendedMin}–${row.recommendedMax} chart range at your effective ${row.effectiveDrawWeight.toFixed(0)} lb.`;
  }
  if (row.verdict === 'slightly-stiff' || row.verdict === 'slightly-weak') {
    const side = row.verdict === 'slightly-stiff' ? 'stiff' : 'weak';
    return `Borderline ${side}. Chart wants ${row.recommendedMin}–${row.recommendedMax}; this spine is within the ±25 real-world tolerance.`;
  }
  const side = row.verdict === 'too-stiff' ? 'stiff' : 'weak';
  return `Too ${side}. Chart wants ${row.recommendedMin}–${row.recommendedMax} at your effective ${row.effectiveDrawWeight.toFixed(0)} lb.`;
}
